import React from 'react';
import { useFormContext } from 'react-hook-form';
import { I18nManager, StyleSheet } from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import { HelperText, Text as RNText, TextInput } from 'react-native-paper';
import styled from 'styled-components/native';

type Props = React.ComponentProps<typeof TextInput> & {
  name: string;
};

type TextProps = {
  isFocused?: boolean;
  isFilled?: boolean;
};

const cellCount = 5;

const Text = styled(RNText)<TextProps>`
  width: 64px;
  height: 64px;
  line-height: 64px;
  font-size: 32px;
  border-width: 1px;
  border-radius: ${({ theme }) => theme.roundness}px;
  margin-horizontal: 6px;
  border-color: ${({ isFocused, isFilled, theme }) =>
    isFocused ? theme.colors.primary : isFilled ? '#607D8B' : '#37474F'};
  text-align: center;
  elevation: ${({ isFocused, isFilled }) => (isFocused ? 8 : isFilled ? 4 : 0)};
`;

const Component: React.FC<Props> = ({ name, ...rest }) => {
  const { register, unregister, setValue, errors } = useFormContext();
  const [data, setData] = React.useState('');
  const ref = useBlurOnFulfill({ value: data, cellCount });
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value: data,
    setValue: setData,
  });

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        codeFieldRoot: {
          marginTop: 20,
          flexDirection: I18nManager.isRTL ? 'row-reverse' : 'row',
          justifyContent: 'center',
        },
      }),
    []
  );

  React.useEffect(() => {
    register(name);
    return () => unregister(name);
  }, [name, register, unregister]);

  React.useEffect(() => {
    setValue(name, data);
  }, [data, name, setValue]);

  const error = errors?.[name];

  return (
    <>
      <CodeField
        ref={ref}
        {...props}
        {...rest}
        value={data}
        onChangeText={setData}
        cellCount={cellCount}
        rootStyle={styles.codeFieldRoot}
        keyboardType="number-pad"
        textContentType="oneTimeCode"
        secureTextEntry
        renderCell={({ index, symbol, isFocused }) => (
          <Text
            key={index}
            isFocused={isFocused}
            isFilled={index > data.length}
            onLayout={getCellOnLayoutHandler(index)}
          >
            {symbol || (isFocused ? <Cursor /> : null)}
          </Text>
        )}
      />
      <HelperText type="error" visible={!!error}>
        {error?.message}
      </HelperText>
    </>
  );
};

Component.displayName = 'OTPInput';

export default Component;
